// apps/client/src/components/common/MfaEnforcementGuard.tsx
//
// The client-side MFA on-ramp for EVERY staff role. Two surfaces, one signal
// pair (tenant mode + live factor state):
//
//   `enforce` + unenrolled  -> non-dismissible interstitial (the wall)
//   `observe` + unenrolled  -> persistent, non-blocking enrolment banner
//
// This guard sits INSIDE ProtectedRoute (needs auth) but OUTSIDE AppLayout.
//
// ─────────────────────────────────────────────────────────────────────────────
// SCOPE: EVERY IN-SCOPE STAFF ROLE (2026-08-19): supersedes the admin-only note
// ─────────────────────────────────────────────────────────────────────────────
// This file used to carry a hard-coded `MFA_REQUIRED_ROLES = ['super_admin',
// 'administrator']` and a note calling the narrower scope deliberate: widening
// it was "a product decision, not a drive-by change". That decision has now been
// made (founder, 2026-08-19: every user is on MFA), so the note is superseded
// and the list is gone. Scope is read from the SSOT the server already uses,
// `classifyMfaEnforcementRole` (packages/shared-utils/src/auth/roleHierarchy.ts,
// also the basis of `beforeSignInMfaGate`), so client and server can no longer
// disagree about who is in scope, and a role added to the union is in scope by
// default (the safe direction). `loyalty_client` is exempt; an absent or
// unrecognised role claim is left alone, exactly as before.
//
// Imported through the `@valutten/shared-utils/roles` subpath, which resolves
// to the single `roleHierarchy` module (zero imports, pure) rather than the
// `auth` barrel that pulls crypto into the browser bundle (see root CLAUDE.md).
// apps/client already ships that subpath (TeamManagementPage, RequireCapability).
//
// ─────────────────────────────────────────────────────────────────────────────
// RECONCILED ONTO THE SSOT (2026-08-17) — two signals changed
// ─────────────────────────────────────────────────────────────────────────────
// 1. "IS THIS TENANT ENFORCING" — was `companies/{id}.requireMfaForAdmins`, a
//    second control plane no sign-in path reads. Now the SSOT tri-state
//    `admin_config/mfaEnforcement__{companyId}`, fetched through the
//    `getMyMfaEnforcementMode` callable because firestore.rules scopes
//    `admin_config` to founders and the client app has none. Only `enforce`
//    walls the app off; `observe` is the enrolment window, and it now has its
//    own surface (the banner below) instead of being silent.
//
// 2. "IS THIS USER ENROLLED" — was `claims.mfaEnabled`, which ONLY the
//    superseded bespoke TOTP flow ever writes. Native GCIP enrolment sets a
//    factor and never sets that claim, so a user who had just enrolled still
//    read as unenrolled and was bounced back to /enroll-2fa forever: an
//    enrolment loop with no exit. Now `hasEnrolledSecondFactor(user)`, the same
//    `enrolledFactors` assertion `beforeSignInMfaGate` makes server-side.
//
// FAIL DIRECTION: OPEN, and loudly. These surfaces are client-side UX, not a
// security boundary (the enforcing controls are the sign-in blocking function
// and the bootstrap gate, both of which read the same SSOT server-side and fail
// CLOSED). Walling every user out of the app because a callable had a blip
// would be a self-inflicted outage protecting nothing.

import React, { useState, useEffect, ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { ShieldAlert, ArrowRight } from 'lucide-react';
import { classifyMfaEnforcementRole } from '@valutten/shared-utils/roles';
import { useAuth } from '../../context/AuthContext';
import { functions } from '../../lib/firebase';
import { hasEnrolledSecondFactor } from '../../lib/nativeMfa';

interface MfaEnforcementGuardProps {
  children: ReactNode;
}

/**
 * The enrolment page, which is a CHILD route of the path this guard wraps
 * (AppRoutes.tsx: `/` → MfaEnforcementGuard → AppLayout → `enroll-2fa`). The
 * interstitial replaces AppLayout, and every child route renders through
 * AppLayout's Outlet — so without this carve-out the "Set Up 2FA" button changes
 * the URL and lands right back on the wall. That was a second enrolment loop,
 * independent of the `mfaEnabled` one: a user who WANTED to comply had no route
 * to the enrolment form. The retired soft-enforcement banner carried the same
 * carve-out; this guard is now the only component that holds it.
 */
const ENROLL_PATH = '/enroll-2fa';

/**
 * Session-scoped dismissal of the `observe` banner. sessionStorage, not local:
 * "Later" must last the working session and no longer, so the nudge returns on
 * the next sign-in rather than being silenced permanently by one click. The
 * stored value is the uid, so a second user signing into the same tab is nudged
 * on their own account rather than inheriting someone else's dismissal.
 */
const BANNER_DISMISSED_KEY = 'valutten.mfaOnrampBanner.dismissedFor';

type EnforcementMode = 'off' | 'observe' | 'enforce';

/**
 * Anything that is not literally `enforce` or `observe` reads as `off`. Keeps
 * the "no false wall, no false nudge" property against a malformed payload
 * (absent field, `ENFORCE`, `on`, undefined data).
 */
function normaliseMode(raw: unknown): EnforcementMode {
  if (raw === 'enforce') return 'enforce';
  if (raw === 'observe') return 'observe';
  return 'off';
}

function readDismissedFor(): string | null {
  try {
    return window.sessionStorage.getItem(BANNER_DISMISSED_KEY);
  } catch {
    // Storage can be unavailable (private mode, blocked cookies). Never fatal:
    // the banner simply stays dismissible only for the current render tree.
    return null;
  }
}

const MfaEnforcementGuard: React.FC<MfaEnforcementGuardProps> = ({ children }) => {
  const { user, claims } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [checking, setChecking] = useState(true);
  const [mode, setMode] = useState<EnforcementMode>('off');
  const [dismissedFor, setDismissedFor] = useState<string | null>(readDismissedFor);

  const role = claims?.role;
  const inScope = classifyMfaEnforcementRole(role) === 'in_scope';
  // Read the factor state off the live Firebase user, so a user who enrols
  // in-session is re-evaluated without needing a token refresh.
  const enrolled = !!user && hasEnrolledSecondFactor(user);
  const uid = user?.uid ?? null;

  useEffect(() => {
    let cancelled = false;

    const checkMfaRequirement = async () => {
      // Out of scope, or already holding a factor: the enrolment check comes
      // FIRST so an enrolled user never pays for the callable round-trip (and
      // can never be looped by it). Neither surface applies to them.
      if (!inScope || enrolled) {
        setMode('off');
        setChecking(false);
        return;
      }

      try {
        const getMode = httpsCallable<void, { mode: EnforcementMode }>(
          functions,
          'getMyMfaEnforcementMode',
        );
        const { data } = await getMode();
        if (cancelled) return;

        // ONE round-trip feeds BOTH surfaces: `enforce` walls, `observe` nudges.
        setMode(normaliseMode(data?.mode));
      } catch (err) {
        // Fail OPEN — see the file header. Logged, never silent.
        console.error('[MfaEnforcementGuard] Could not read MFA enforcement mode:', err);
        if (!cancelled) setMode('off');
      } finally {
        if (!cancelled) setChecking(false);
      }
    };

    setChecking(true);
    checkMfaRequirement();

    return () => {
      cancelled = true;
    };
  }, [inScope, enrolled]);

  const dismissBanner = () => {
    setDismissedFor(uid);
    try {
      if (uid) window.sessionStorage.setItem(BANNER_DISMISSED_KEY, uid);
    } catch {
      // Non-fatal: the banner is hidden for this render tree regardless.
    }
  };

  /**
   * Every child render goes through here. AppLayout's root is `h-full` and
   * takes its height from THIS element, so the wrapper is unconditional: with
   * no banner the app is exactly 100vh as before, and with one it is 100vh
   * minus the banner instead of overflowing the viewport and clipping the
   * bottom of the sidebar and the content scroller.
   *
   * `bg-gray-900` is NOT decoration: it is the same background AppLayout's root
   * paints (`flex h-full bg-gray-900 text-white`, AppLayout.tsx), and it has to
   * live HERE because the banner renders ABOVE AppLayout, outside anything
   * AppLayout paints. Without it the banner's translucent surface composited
   * over the bare white document body, which is exactly what shipped: a pale
   * beige strip with unreadable text (founder screenshots, Timms at `observe`,
   * 2026-08-22). Painting the shell background on the wrapper makes the banner
   * render identically whether or not AppLayout has painted yet. Pinned by
   * MfaEnforcementGuard.test.tsx ("shell background").
   */
  const withShell = (banner: ReactNode = null) => (
    <div
      className="flex flex-col h-screen overflow-hidden bg-gray-900"
      aria-busy={checking || undefined}
    >
      {banner}
      <div className="flex-1 min-h-0">{children}</div>
    </div>
  );

  // NO spinner while the mode read is in flight (F-01). This guard used to
  // return <Loading /> here, which blocked the WHOLE app behind a Gen 2 callable
  // for every unenrolled in-scope user at EVERY tenant, `off` included. When the
  // guard covered two admin roles that was a narrow cost; now that it covers
  // every staff role it would have made a cold callable a company-wide stall for
  // people whose tenant has MFA switched off entirely.
  //
  // Rendering children first is safe because THIS IS NOT THE SECURITY CONTROL.
  // `beforeSignInMfaGate` refuses the session server-side and fails CLOSED; a
  // user who must not be in the app never gets a token to render with. So the
  // worst case here is cosmetic: under `enforce`, an exempted session may show a
  // frame of app content before the wall replaces it. That flash is an accepted
  // trade for not gating every render on a network round-trip. `aria-busy` marks
  // the in-flight window for assistive tech.
  const mustEnroll = mode === 'enforce';

  // Always let the enrolment page itself through — it is the ONLY exit from the
  // interstitial, and it lives inside this guard (see ENROLL_PATH).
  if (checking) {
    return withShell();
  }

  if (mustEnroll && location.pathname === ENROLL_PATH) {
    return withShell();
  }

  if (mustEnroll) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900 p-4">
        <div className="max-w-md w-full bg-gray-800 rounded-xl shadow-2xl p-8 text-center">
          <div className="flex justify-center mb-6">
            <div className="bg-amber-500/10 p-4 rounded-full">
              <ShieldAlert className="w-12 h-12 text-amber-400" />
            </div>
          </div>

          <h2 className="text-2xl font-bold text-white mb-3">
            Two-Factor Authentication Required
          </h2>

          <p className="text-gray-400 mb-6 leading-relaxed">
            Your organisation requires two-factor authentication. Please enrol in 2FA to
            continue accessing Valutten.
          </p>

          <button
            onClick={() => navigate('/enroll-2fa')}
            className="w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors"
          >
            Set Up 2FA
            <ArrowRight className="w-5 h-5" />
          </button>

          <p className="text-gray-500 text-sm mt-4">
            Contact your super admin if you need assistance.
          </p>
        </div>
      </div>
    );
  }

  // `observe`: the enrolment window. Nudge, never block, and never on the
  // enrolment page itself (the user is already doing the thing it asks for).
  const showBanner =
    mode === 'observe' &&
    location.pathname !== ENROLL_PATH &&
    dismissedFor !== uid;

  if (!showBanner) {
    return withShell();
  }

  // Surface vocabulary is the app shell's own, not a bespoke amber block: the
  // card sits on `bg-gray-800` (the same elevated surface the wall's card and
  // the Settings cards use over the `bg-gray-900` shell), body copy is the app's
  // primary text token `text-white`, and amber is demoted to an ACCENT on the
  // icon and the border only. The previous `bg-amber-900/30 ... text-amber-200`
  // treatment was the readability defect: translucent amber over an unpainted
  // body, with low-contrast amber body copy on top of it.
  //
  // Buttons reuse the established pair: the wall's primary (`bg-blue-600`) and a
  // quiet ghost that stays legible on the dark surface (`text-gray-300` to
  // `text-white`, previously `text-amber-200/70`, which was near-invisible).
  return withShell(
    <div className="flex-shrink-0 px-4 pt-4 md:px-6 md:pt-6">
      <div
        role="status"
        className="flex flex-col sm:flex-row sm:items-center gap-3 px-4 py-2.5 rounded-lg bg-gray-800 border border-amber-500/40 text-white shadow-lg"
      >
        <ShieldAlert className="flex-shrink-0 text-amber-400" size={20} />
        <div className="flex-1 text-sm leading-snug">
          Your workspace is moving to two-factor authentication. Set up your authenticator
          now so you keep access when it becomes required.
        </div>
        <div className="flex-shrink-0 flex items-center gap-2">
          <button
            onClick={() => navigate(ENROLL_PATH)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors"
          >
            Set up 2FA
            <ArrowRight size={14} />
          </button>
          <button
            onClick={dismissBanner}
            className="px-3 py-1.5 text-xs font-medium text-gray-300 hover:text-white transition-colors"
          >
            Later
          </button>
        </div>
      </div>
    </div>,
  );
};

export default MfaEnforcementGuard;
